/**
 * Mailchimp Error Parser Module (Story 12.1, Task 2)
 *
 * Maps Mailchimp API error responses to standardized error types.
 *
 * Mailchimp API error response shape:
 * { type: string, title: string, status: number, detail: string, instance: string }
 *
 * See: https://mailchimp.com/developer/marketing/docs/errors/
 */

// ============================================================================
// Types
// ============================================================================

export type MailchimpErrorType =
  | "token_expired"
  | "permission_denied"
  | "rate_limited"
  | "not_found"
  | "provider_unavailable"
  | "unknown";

export interface MailchimpApiError {
  type: MailchimpErrorType;
  message: string;
  status: number | null;
  retryable: boolean;
}

interface MailchimpErrorResponse {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
}

// ============================================================================
// Status Code Mapping
// ============================================================================

const STATUS_CODE_MAP: Record<number, { type: MailchimpErrorType; retryable: boolean }> = {
  401: { type: "token_expired", retryable: false },
  403: { type: "permission_denied", retryable: false },
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limited", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// Error Parser (Task 2.2)
// ============================================================================

/**
 * Parses a Mailchimp API error response into a standardized error type.
 *
 * @param responseBody - The parsed JSON body from a Mailchimp API response
 * @returns A standardized Mailchimp API error, or null if no error detected
 */
export function parseMailchimpApiError(
  responseBody: unknown
): MailchimpApiError | null {
  const body = responseBody as MailchimpErrorResponse;

  if (!body?.status || (body.status >= 200 && body.status < 300)) {
    return null;
  }

  const mapping = STATUS_CODE_MAP[body.status];
  const message = body.detail || body.title || `Mailchimp API error: HTTP ${body.status}`;
  const status = body.status;

  if (mapping) {
    return {
      type: mapping.type,
      message,
      status,
      retryable: mapping.retryable,
    };
  }

  // Unknown status code
  return {
    type: "unknown",
    message,
    status,
    retryable: false,
  };
}
